import { Button, Card, Paragraph, Tooltip } from '@digdir/designsystemet-react';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilesIcon } from '@navikt/aksel-icons';

import { ApiRequestError, getRegistrationSupportDetails } from '../services/api';

type ErrorSupportDetailsProps = {
  // Pass the full error to copy rich support details (step, route, errorCode, status);
  // pass a bare traceId when that is all the call site has.
  error?: ApiRequestError | null;
  traceId?: string | null;
  registrationId?: string;
  style?: React.CSSProperties;
};

const supportSectionStyle = {
  display: 'grid',
  gap: '0.25rem',
  margin: `var(--ds-size-4) var(--ds-size-4) var(--ds-size-4) calc(var(--ds-size-4) * -1)`,
  position: 'relative',
} as const;

const supportValueStyle = {
  fontFamily: 'monospace',
  wordBreak: 'break-word'
} as const;

const tooltipButtonStyle = {
  position: 'absolute',
  top: 0,
  right: `var(--ds-size-4)`,
} as const;

/**
 * Shows the trace reference (and registration id when known) plus a copy-to-clipboard button so
 * users can hand the details to support. Renders nothing when there is no trace id.
 */
export const ErrorSupportDetails: FC<ErrorSupportDetailsProps> = ({ error, traceId, registrationId, style }) => {
  const { t } = useTranslation();
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'success' | 'failed'>('idle');
  const resetTimeout = useRef<number>();

  const resolvedTraceId = (error?.traceId ?? traceId ?? '').trim();
  const supportDetails = error
    ? getRegistrationSupportDetails(error, registrationId).supportDetails
    : resolvedTraceId
      ? `traceId: ${resolvedTraceId}`
      : '';

  const copySupportDetails = async () => {
    if (!supportDetails) {
      return;
    }

    try {
      await navigator.clipboard.writeText(supportDetails);
      setCopyFeedback('success');
    } catch {
      setCopyFeedback('failed');
    }

    window.clearTimeout(resetTimeout.current);
    resetTimeout.current = window.setTimeout(() => setCopyFeedback('idle'), 2500);
  };

  if (!resolvedTraceId) {
    return null;
  }

  return (
    <div data-color="fjellgronn" style={style}>
      <Paragraph>{t('generalError.supportDescription')}</Paragraph>
      <Card style={supportSectionStyle}>
        {resolvedTraceId && (
          <Paragraph data-size="xs">
            <strong>{t('generalError.traceIdLabel')}:</strong> <span style={supportValueStyle}>{resolvedTraceId}</span>
          </Paragraph>
        )}
        {registrationId && (
          <Paragraph data-size="xs">
            <strong>{t('generalError.registrationIdLabel')}:</strong>{' '}
            <span style={supportValueStyle}>{registrationId}</span>
          </Paragraph>
        )}
        <Tooltip content={
          t(
            copyFeedback === 'success'
              ? 'generalError.copySupportDetailsSuccess'
              : copyFeedback === 'failed'
                ? 'generalError.copySupportDetailsFailed'
                : 'generalError.copySupportDetails'
          )
        }
          style={tooltipButtonStyle}>
          <Button icon aria-label='Kopier' variant="secondary" onClick={copySupportDetails}>
            <FilesIcon aria-hidden />
          </Button>
        </Tooltip>
      </Card>
    </div >
  );
};
